import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  TemplateRef,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { MatSelect } from '@angular/material/select';

@Component({
  selector: 'app-select-with-footer',
  templateUrl: './select-with-footer.component.html',
  styleUrls: ['./select-with-footer.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectWithFooterComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SelectWithFooterComponent),
      multi: true,
    },
  ],
})
export class SelectWithFooterComponent
  implements OnInit, OnDestroy, ControlValueAccessor, Validator
{
  @Input() options: any[] = [];
  @Input() placeholder = '';
  @Input() multiple = false;
  @Input() required = false;
  @Input() panelClass: string | string[] = 'matselectopenPermissionpanel';
  @Input() optionLabelKey = '';
  @Input() optionValueKey = '';
  @Input() selectClass = '';
  @Input() disableOptionCentering = true;
  @Input() showFooter = true;
  @Input() okLabel = 'Ok';
  @Input() closeLabel = 'Close';
  @Input() compareWith?: (first: any, second: any) => boolean;
  @Input() optionTooltipFn?: (option: any) => string;
  @Input() optionDisabledFn?: (option: any) => boolean;
  @Input() optionTemplate?: TemplateRef<any>;
  @Input() errorMessage = '';
  @Input() showError = false;
  @Output() selectionChange = new EventEmitter<any>();
  @ViewChild('selectRef') selectRef?: MatSelect;

  control = new FormControl<any>(null);
  readonly defaultCompareWith = (first: any, second: any): boolean =>
    first === second;

  private originalClose: (() => void) | null = null;
  private outsideClickHandler: ((e: MouseEvent) => void) | null = null;
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidationChange: () => void = () => {};

  ngOnInit(): void {
    this.control.valueChanges.subscribe((value) => {
      this.onChange(value);
      this.onTouched();
      this.selectionChange.emit(value);
      this.onValidationChange();
      if (!this.multiple && this.selectRef?.panelOpen) {
        setTimeout(() => {
          if (this.selectRef?.panelOpen) {
            (this.selectRef as any)._overlayDir?.overlayRef?.updatePosition();
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.restoreMatSelectClose();
  }

  writeValue(value: any): void {
    this.control.setValue(this.normalizeIncomingValue(value), {
      emitEvent: false,
    });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.control.disable({ emitEvent: false });
      return;
    }

    this.control.enable({ emitEvent: false });
  }

  validate(_: AbstractControl): ValidationErrors | null {
    if (!this.required || !this.isEmptyValue(this.control.value)) {
      return null;
    }

    return { required: true };
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidationChange = fn;
  }

  onOpenedChange(isOpen: boolean): void {
    if (!isOpen) {
      this.restoreMatSelectClose();
      return;
    }

    if (this.showFooter && !this.multiple) {
      this.preventMatSelectClose();
    }
  }

  close(select: MatSelect, event: Event): void {
    event.stopPropagation();
    this.restoreMatSelectClose();
    select.close();
  }

  hasSelection(): boolean {
    return !this.isEmptyValue(this.control.value);
  }

  get resolvedCompareWith(): (first: any, second: any) => boolean {
    return this.compareWith ?? this.defaultCompareWith;
  }

  getOptionLabel(option: any): string {
    if (this.optionLabelKey && option && typeof option === 'object') {
      return `${option[this.optionLabelKey] ?? ''}`;
    }

    return `${option ?? ''}`;
  }

  getOptionValue(option: any): any {
    if (this.optionValueKey && option && typeof option === 'object') {
      return option[this.optionValueKey];
    }

    return option;
  }

  getOptionTooltip(option: any): string {
    return this.optionTooltipFn ? this.optionTooltipFn(option) : '';
  }

  isOptionDisabled(option: any): boolean {
    return this.optionDisabledFn ? this.optionDisabledFn(option) : false;
  }

  private normalizeIncomingValue(value: any): any {
    if (value === null || value === undefined) {
      return this.multiple ? [] : null;
    }

    if (this.multiple && !Array.isArray(value)) {
      return [value];
    }

    return value;
  }

  private isEmptyValue(value: any): boolean {
    if (Array.isArray(value)) {
      return value.length === 0;
    }

    return value === null || value === undefined || value === '';
  }

  private preventMatSelectClose(): void {
    if (this.selectRef && !this.originalClose) {
      this.originalClose = this.selectRef.close.bind(this.selectRef);
      this.selectRef.close = () => {};

      this.outsideClickHandler = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        if (target?.classList?.contains('cdk-overlay-backdrop')) {
          this.restoreMatSelectClose();
          this.selectRef?.close();
        }
      };
      setTimeout(() => {
        document.addEventListener('click', this.outsideClickHandler!, true);
      }, 0);
    }
  }

  private restoreMatSelectClose(): void {
    if (this.selectRef && this.originalClose) {
      this.selectRef.close = this.originalClose;
      this.originalClose = null;
    }
    if (this.outsideClickHandler) {
      document.removeEventListener('click', this.outsideClickHandler, true);
      this.outsideClickHandler = null;
    }
  }
}
